import { Monitor } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { desktopOnly, PHONE_HOME } from "../phoneSurface";
import { useViewport } from "../ViewportProvider";

/**
 * What the phone app says at an address it does not serve.
 *
 * The alternative was porting each of these screens badly, and M12 is explicit that
 * Transcript Review (UI-12) and the Debug tab (ARCH-8) are desktop-only and stay that way —
 * the same is true of the recordings list, the insights ledger, the entity table, the
 * Assistant and Settings, none of which the phone handoff draws.
 *
 * A link to one of them still resolves rather than 404s, because a chat citation or a
 * shared address should not break on a phone. It arrives here, is told which screen this is
 * and why it lives at a desk, and is offered the desktop layout on this device — which is a
 * real way through, not a consolation: the desktop shell scales, it is merely not designed
 * for a thumb.
 */
export function DesktopOnly({ standalone = false }: { standalone?: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setOverride } = useViewport();

  const screen = desktopOnly(pathname) ?? {
    name: "This screen",
    why: "It is not part of the phone app.",
  };

  return (
    <div
      className={
        standalone
          ? // Outside the shell (Transcript Review), so it brings its own page.
            "flex h-full flex-col items-center justify-center gap-3 bg-[var(--surface-0)] px-6 py-12 text-center"
          : "flex flex-col items-center gap-3 px-6 py-12 text-center"
      }
    >
      <Monitor size={28} strokeWidth={1.75} className="text-[var(--text-muted)]" aria-hidden />

      <p className="text-[16px] font-bold text-[var(--text-primary)]">{screen.name} is on the desktop app</p>

      <p className="text-[14px] leading-[1.5] text-[var(--text-muted)] [text-wrap:pretty]">{screen.why}</p>

      <button
        type="button"
        onClick={() => setOverride("desktop")}
        className="mt-2 h-12 w-full rounded-[var(--radius-md)] border border-[var(--border-default)] text-[15px] font-semibold text-[var(--text-primary)]"
      >
        Show the desktop layout here
      </button>

      <button
        type="button"
        onClick={() => navigate(PHONE_HOME)}
        className="h-11 w-full text-[13px] text-[var(--text-muted)]"
      >
        Back to your world view
      </button>
    </div>
  );
}
